import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface AdminEditModeContextType {
  isAdmin: boolean;
  isEditMode: boolean;
  toggleEditMode: () => void;
  setEditMode: (on: boolean) => void;
}

const AdminEditModeContext = createContext<AdminEditModeContextType>({
  isAdmin: false,
  isEditMode: false,
  toggleEditMode: () => {},
  setEditMode: () => {},
});

export const useAdminEditMode = () => useContext(AdminEditModeContext);

export const AdminEditModeProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      setIsEditMode(false);
      return;
    }

    const checkAdmin = async () => {
      try {
        const { data, error } = await supabase.rpc("has_role", {
          _user_id: user.id,
          _role: "admin",
        });

        if (error) throw error;
        setIsAdmin(!!data);
      } catch {
        setIsAdmin(false);
      }
    };

    checkAdmin();
  }, [user]);

  const toggleEditMode = useCallback(() => {
    setIsEditMode((prev) => !prev);
  }, []);

  return (
    <AdminEditModeContext.Provider
      value={{ isAdmin, isEditMode, toggleEditMode, setEditMode: setIsEditMode }}
    >
      {children}
    </AdminEditModeContext.Provider>
  );
};
